import { useCallback, useMemo } from "react";
import { UserApiClient } from "@/lib/api/clients/user-api-client";
import { UserService } from "@/lib/services/user-service";
import {
  User,
  UpdateUserRequest,
  Organization,
} from "@/lib/types/api/user-types";
import { toast } from "@/hooks/use-toast";

/**
 * React hook for user service operations
 * Follows SOC: Separates service logic from UI components
 * Follows DRY: Centralized error handling and service instantiation
 */
export function useUserService() {
  // Create service instance with dependency injection
  const userService = useMemo(() => {
    const userApiClient = new UserApiClient();
    return new UserService(userApiClient);
  }, []);

  const getCurrentUser = useCallback(async (): Promise<User | null> => {
    const result = await userService.getCurrentUser();
    if (!result.success) {
      toast({
        title: "Error",
        description: result.error?.message || "Failed to get user profile",
        variant: "destructive",
      });
      return null;
    }
    return result.data || null;
  }, [userService]);

  const updateProfile = useCallback(
    async (data: UpdateUserRequest): Promise<User | null> => {
      const result = await userService.updateProfile(data);
      if (!result.success) {
        toast({
          title: "Error",
          description: result.error?.message || "Failed to update profile",
          variant: "destructive",
        });
        return null;
      }
      toast({
        title: "Success",
        description: "Profile updated successfully",
      });
      return result.data || null;
    },
    [userService]
  );

  const deleteAccount = useCallback(async (): Promise<boolean> => {
    const result = await userService.deleteAccount();
    if (!result.success) {
      toast({
        title: "Error",
        description: result.error?.message || "Failed to delete account",
        variant: "destructive",
      });
      return false;
    }
    toast({
      title: "Success",
      description: "Account deleted successfully",
    });
    return true;
  }, [userService]);

  const searchUsers = useCallback(
    async (query: string): Promise<any[]> => {
      const result = await userService.searchUsers(query);
      if (!result.success) {
        toast({
          title: "Error",
          description: result.error?.message || "Failed to search users",
          variant: "destructive",
        });
        return [];
      }
      return result.data || [];
    },
    [userService]
  );

  // Utility methods from service
  const isProfileComplete = useCallback(
    (user: User): boolean => {
      return userService.isProfileComplete(user);
    },
    [userService]
  );

  const getMissingProfileFields = useCallback(
    (user: User): string[] => {
      return userService.getMissingProfileFields(user);
    },
    [userService]
  );

  const isValidEmail = useCallback(
    (email: string): boolean => {
      return userService.isValidEmail(email);
    },
    [userService]
  );

  return {
    // Core API operations
    getCurrentUser,
    updateProfile,
    deleteAccount,
    searchUsers,

    // Utility methods
    isProfileComplete,
    getMissingProfileFields,
    isValidEmail,
  };
}
